import React, { Fragment, useState, useEffect } from "react";
import { Col, FormGroup, Label, Row, Button, Table } from 'reactstrap';
import Select, { components } from "react-select";

const Category = (props) => {
    const { 
        getValues, 
        setValue, 
        trigger, 
        errors, 
        settingProductCategories,
        settingProductSubCategories,
        settingCatStyles,
        setSettingCatStyles,
        settingStyles
    } = props;
    
    // New: duplicate tracking state
    const [duplicateIndices, setDuplicateIndices] = useState([]);

    const [categoryRows, setCategoryRows] = useState([
        { id: Date.now(), category_id: '', style_ids: [] }
    ]);

    // Initialize rows from form values if they exist
    useEffect(() => {
        const existingData = getValues("category_styles");
        if (existingData && existingData.length > 0) {
            setCategoryRows(existingData);
        }
    }, []);

    // Update form values whenever rows change
    useEffect(() => {
        setValue("category_styles", categoryRows);
        trigger("category_styles");
    }, [categoryRows]);
    
    // New: detect duplicate rows & expose a form-level flag
    useEffect(() => {
        const keyMap = {};
        (categoryRows || []).forEach((row, idx) => {
            const key = `${row.category_id || ''}`;
            const isEmpty = !row.category_id && (!row.style_ids || row.style_ids.length === 0);
            if (isEmpty) return; // ignore totally empty rows
            if (!keyMap[key]) keyMap[key] = [];
            keyMap[key].push(idx);
        });
        const duplicates = new Set();
        Object.values(keyMap).forEach(arr => { if (arr.length > 1) arr.forEach(i => duplicates.add(i)); });
        const dupArray = Array.from(duplicates);
        setDuplicateIndices(dupArray);
        const hasDuplicates = dupArray.length > 0;
        if (setValue) setValue("cat_style_has_duplicates", hasDuplicates);
        if (trigger) trigger("cat_style_has_duplicates");
    }, [categoryRows, setValue, trigger]);

    const addRow = () => {
        setCategoryRows([...categoryRows, { id: Date.now(), category_id: '', style_ids: [] }]);
    };

    const deleteRow = (id) => {
        if (categoryRows.length > 1) {
            setCategoryRows(categoryRows.filter(row => row.id !== id));
        }
    };

    const updateRow = (id, field, value) => {
        setCategoryRows(categoryRows.map(row => 
            row.id === id ? { ...row, [field]: value } : row
        ));
    };

    return (
        <Fragment>
            <Label className="form-label ai-form-heading">Product Category Details</Label>
            <Row>
                {/* Product Category (Single Select) */}
                <Col md="6">
                    <FormGroup>
                        <Label className="form-label">Product Category</Label>
                        <Select
                            placeholder="Select Product Category"
                            options={settingProductCategories?.filter(opt => opt.type_id === getValues("product_type")) || []}
                            getOptionValue={option => option.id}
                            getOptionLabel={option => option.name}
                            value={settingProductCategories?.find(opt => opt.id === getValues("product_category")) || null}
                            onChange={selected => { 
                                setValue("product_category", selected ? selected.id : ''); 
                                trigger("product_category"); 
                                setSettingCatStyles(settingStyles?.filter(opt => opt.category_id === (selected ? selected.id : '')) || []);
                                // Reset all rows when category changes
                                setCategoryRows([{ id: Date.now(), category_id: '', style_ids: [] }]);
                            }}
                            className={errors.product_category ? "is-invalid" : ""}
                            isClearable={true}
                            components={{
                                IndicatorSeparator: () => null,
                                DropdownIndicator: (props) => {
                                    return getValues("product_category") ? null : <components.DropdownIndicator {...props} />;
                                }
                            }}
                        />
                        <div className="invalid-feedback">{errors.product_category?.message}</div>
                    </FormGroup>
                </Col>
            </Row>

            {/* Sub Category and Styles Table */}
            <Row>
                <Col md="12">
                    <table className="table table-bordered table-sm align-middle">
                        <thead>
                            <tr>
                                <th style={{ width: '45%' }}>Product Sub Category</th>
                                <th style={{ width: '45%' }}>Styles</th>
                                <th style={{ width: '10%', textAlign: 'center' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categoryRows.map((row, idx) => {
                                const isDuplicate = duplicateIndices.includes(idx);

                                const selectErrorStyles = {
                                    control: (base, state) => ({
                                        ...base,
                                        borderColor: isDuplicate ? '#dc3545' : base.borderColor,
                                        boxShadow: isDuplicate ? '0 0 0 1px rgba(220,53,69,0.12)' : base.boxShadow,
                                        '&:hover': { borderColor: isDuplicate ? '#dc3545' : base.borderColor },
                                    })
                                };

                                return (
                                    <tr key={row.id}>
                                        <td>
                                            <Select
                                                placeholder="Select Product Sub Category"
                                                options={settingProductSubCategories?.filter(opt => opt.category_id === getValues("product_category")) || []}
                                                getOptionValue={option => option.id}
                                                getOptionLabel={option => option.name}
                                                value={settingProductSubCategories?.find(opt => opt.id === row.category_id) || null}
                                                onChange={selected => {
                                                    updateRow(row.id, 'category_id', selected ? selected.id : '');
                                                }}
                                                isDisabled={!getValues("product_category")}
                                                styles={selectErrorStyles}
                                            />
                                        </td>
                                        <td>
                                            <Select
                                                placeholder="Select Styles"
                                                isMulti
                                                options={settingCatStyles?.filter(opt => opt.category_id === getValues("product_category")) || []}
                                                getOptionValue={option => option.id}
                                                getOptionLabel={option => option.name}
                                                value={settingCatStyles?.filter(opt => (row.style_ids || []).includes(opt.id))}
                                                onChange={selected => {
                                                    updateRow(row.id, 'style_ids', selected ? selected.map(s => s.id) : []);
                                                }}
                                                isDisabled={!getValues("product_category")}
                                            />
                                        </td>
                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                            {categoryRows.length > 1 && (
                                                <Button
                                                    color="danger"
                                                    size="sm"
                                                    onClick={() => deleteRow(row.id)}
                                                    title="Delete Row"
                                                >
                                                    <i className="fa fa-trash" />
                                                </Button>
                                            )}
                                            {isDuplicate && <div className="text-danger small mt-1">Duplicate row</div>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <Button
                        color="success"
                        size="sm"
                        onClick={addRow}
                        disabled={!getValues("product_category")}
                        className="mt-2"
                    >
                        <i className="fa fa-plus mr-1" /> Add Row
                    </Button>
                    {errors.category_styles && (
                        <div className="invalid-feedback d-block">{errors.category_styles?.message}</div>
                    )}
                </Col>
            </Row>
        </Fragment>
    );
};

export default Category;
